import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';

const SITE_URL = 'https://www.ifa.com';
const ORG_ID = `${SITE_URL}/#organization`;
const WEBSITE_ID = `${SITE_URL}/#website`;
const LOGO_URL = 'https://services.ifa.com/art/images/1614_2019-5-7-11-52-54.jpg';
const ORG_IMAGE_URL = 'https://services.ifa.com/art/images/399_2013-10-1-16-47-5.jpg';
const ORG_DESCRIPTION = 'IFA is a Registered Investment Adviser with the U.S. Securities and Exchange Commission that provides investment advice to individuals, trusts, corporations, non-profits, and public and private institutions.';

const POSTAL_ADDRESS = {
  '@type': 'PostalAddress',
  streetAddress: '19200 Von Karman Ave. Suite 150',
  addressLocality: 'Irvine',
  addressRegion: 'CA',
  postalCode: '92612',
  addressCountry: 'US'
};

const FOUNDER = {
  '@type': 'Person',
  '@id': `${SITE_URL}/mark#person`,
  name: 'Mark T. Hebner',
  url: `${SITE_URL}/mark`
};

export interface AdvisorSchemaInput {
  name: string;
  url: string;
  pageTitle: string;
  description: string;
  jobTitle: string;
  image?: string;
  givenName?: string;
  familyName?: string;
  email?: string;
  telephone?: string;
  credentials?: string[];
  colleagues?: { name: string; url?: string }[];
}

/** Compact publisher entity embedded in page-level schemas; the full entity lives in organizationGraph(). */
const PUBLISHER = {
  '@type': 'Organization',
  '@id': ORG_ID,
  name: 'Index Fund Advisors, Inc.',
  alternateName: 'IFA',
  url: SITE_URL,
  logo: { '@type': 'ImageObject', url: LOGO_URL },
  image: ORG_IMAGE_URL,
  foundingDate: '1999-03-01',
  founder: FOUNDER,
  address: POSTAL_ADDRESS,
  description: ORG_DESCRIPTION
};

/** "first last" -> profile slug; keep in sync with the advisor routes in pages.routing.component.ts. */
const IFA_AUTHOR_SLUGS: Record<string, string> = {
  'mark hebner': 'mark',
  'wes long': 'wes',
  'denise delaney': 'denise',
  'mary brunson': 'mary',
  'derick kann': 'derick',
  'venkat yarlagadda': 'venkat',
  'gordon shuler': 'gordon',
  'neal rutter': 'neal',
  'daniel chong': 'daniel',
  'kerrie lloyd': 'kerrie'
};

/** "Mark T. Hebner" -> "mark hebner" */
function nameKey(name: string): string {
  const parts = name.toLowerCase().replace(/[^a-z\s-]/g, ' ').split(/\s+/).filter(p => p.length > 0);
  return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1]}` : parts.join('');
}

function authorEntity(name: string, archiveUrl?: string): Record<string, any> {
  const slug = IFA_AUTHOR_SLUGS[nameKey(name)];
  if (slug) {
    return { '@type': 'Person', '@id': `${SITE_URL}/${slug}#person`, name, url: `${SITE_URL}/${slug}`, worksFor: { '@id': ORG_ID } };
  }
  const person: Record<string, any> = {
    '@type': 'Person',
    '@id': `${SITE_URL}/#person-${nameKey(name).replace(/\s+/g, '-')}`,
    name
  };
  if (archiveUrl) {
    person['url'] = archiveUrl;
  }
  return person;
}

/** "Mark Hebner and Murray Coleman" -> [Person, Person] */
function authorsFrom(author: string, archiveUrl?: string): Record<string, any>[] {
  return (author || '')
    .split(/\s+and\s+|,|&/i)
    .map(n => n.split('|')[0].trim())
    .filter(n => n.length > 0)
    .map(name => authorEntity(name, archiveUrl));
}

/** The articles list filters on the exact author string, so co-authors share one archive URL. */
function articleArchiveUrl(author: string): string | undefined {
  return author ? `${SITE_URL}/articles?author=${encodeURIComponent(author)}` : undefined;
}

@Injectable({
  providedIn: 'root'
})
export class JsonldserviceService {

  static scriptType = 'application/ld+json';
  static readonly PAGE_CLASS = 'structured-data';
  static readonly ORG_CLASS = 'structured-data-org';

constructor(
  @Inject(DOCUMENT) private _document: Document,
  @Inject(PLATFORM_ID) private platformId: Object
) { }

   /** Site-wide entity graph. Emitted once from AppComponent and never removed on navigation. */
   static organizationGraph = () => {
     return {
       '@context': 'https://schema.org',
       '@graph': [
         {
           ...PUBLISHER,
           '@type': ['Organization', 'FinancialService'],
           legalName: 'Index Fund Advisors, Inc.',
           telephone: '+1-888-643-3133',
           faxNumber: '+1-949-502-0048',
           areaServed: 'US',
           knowsAbout: [
             'Index funds',
             'Passive investing',
             'Fama-French factor investing',
             'Fiduciary financial advice',
             'Retirement planning',
             'Tax planning',
             'Modern Portfolio Theory',
             'Efficient Market Hypothesis'
           ],
           sameAs: [
             'https://www.linkedin.com/company/index-funds-advisors-inc-',
             'https://www.youtube.com/user/IndexFundsAdvisors',
             'https://twitter.com/ifadotcom',
             'https://www.facebook.com/ifadotcom',
             'https://www.instagram.com/indexfundadvisors/',
             'https://adviserinfo.sec.gov/firm/summary/109143'
           ]
         },
         {
           '@type': 'WebSite',
           '@id': WEBSITE_ID,
           url: SITE_URL,
           name: 'IFA.com',
           publisher: { '@id': ORG_ID },
           inLanguage: 'en-US',
           potentialAction: {
             '@type': 'SearchAction',
             target: { '@type': 'EntryPoint', urlTemplate: `${SITE_URL}/articles?q={search_term_string}` },
             'query-input': 'required name=search_term_string'
           }
         }
       ]
     };
   };

   static pageSchema = (name : string, url :string, description: string) => 
     { 
       return {
         '@context'  :
         'https://schema.org',
       '@type'     :
         'WebPage',
       url         : url,
       name        : name,
       description : description,
       inLanguage  : 'en-US',
       isPartOf    : { '@id': WEBSITE_ID },
       'publisher' : PUBLISHER
       }
     };

   static articleSchema = (name : string, url :string, id : string, publishDate: string, modifiedDate: string, author: string, keywords: string, description: string, text: string, imageUrl: string, phoneLocal: string, faxNumber: string, mailingAddress: string, views:number) => {
     return {
       '@context'  :
         'https://schema.org',
       '@type'     :
         'Article',
       '@id'       : url + '#article',
       mainEntityOfPage: { '@type': 'WebPage', '@id': url },
       url         : url,
       name        : name,
       headline    : name, 
       identifier  : id,
       datePublished: publishDate,
       dateModified: modifiedDate,
       author      : authorsFrom(author, articleArchiveUrl(author)),
       keywords    : keywords,
       description : description,
       articleBody : text,
       thumbnailUrl: imageUrl,
       image       : imageUrl,
       inLanguage  : 'en-US',
       isAccessibleForFree: true,
       'publisher' : { ...PUBLISHER, telephone: phoneLocal, faxNumber: faxNumber },
       'interactionStatistic':[{
           '@type':    'InteractionCounter',
           'interactionType': 'https://schema.org/ReadAction',
           'userInteractionCount': views
       }]
     };
   };

   //SEE: https://developers.google.com/search/docs/appearance/structured-data/profile-page
   /** ProfilePage + Person + BreadcrumbList for an advisor profile. Only pass verified data; empty fields are dropped. */
   static advisorSchema = (a: AdvisorSchemaInput) =>
     {
       const person: Record<string, any> = {
         '@type'       : 'Person',
         '@id'         : a.url + '#person',
         name          : a.name,
         identifier    : a.url,
         url           : a.url,
         description   : a.description, //plainText
         jobTitle      : a.jobTitle,
         worksFor      : PUBLISHER
       };
       if (a.givenName)  person['givenName']  = a.givenName;
       if (a.familyName) person['familyName'] = a.familyName;
       if (a.image)      person['image']      = { '@type': 'ImageObject', url: a.image, caption: a.name };
       if (a.email)      person['email']      = 'mailto:' + a.email;
       if (a.telephone)  person['telephone']  = a.telephone;
       if (a.credentials && a.credentials.length) {
         person['honorificSuffix'] = a.credentials.join(', ');
         person['hasCredential'] = a.credentials.map(c => ({ '@type': 'EducationalOccupationalCredential', name: c }));
       }
       if (a.colleagues && a.colleagues.length) {
         person['colleague'] = a.colleagues.map(c => ({ '@type': 'Person', name: c.name, ...(c.url ? { url: c.url } : {}) }));
       }
       return {
         '@context': 'https://schema.org',
         '@graph': [
           {
             '@type'     : 'ProfilePage',
             '@id'       : a.url,
             url         : a.url,
             name        : a.pageTitle,
             inLanguage  : 'en-US',
             isPartOf    : { '@id': WEBSITE_ID },
             breadcrumb  : { '@id': a.url + '#breadcrumb' },
             mainEntity  : { '@id': a.url + '#person' }
           },
           person,
           {
             '@type': 'BreadcrumbList',
             '@id'  : a.url + '#breadcrumb',
             itemListElement: [
               { '@type': 'ListItem', position: 1, name: 'Home',            item: SITE_URL + '/' },
               { '@type': 'ListItem', position: 2, name: 'About IFA',       item: SITE_URL + '/about' },
               { '@type': 'ListItem', position: 3, name: 'Wealth Advisors', item: SITE_URL + '/about/advisors' },
               { '@type': 'ListItem', position: 4, name: a.name }
             ]
           }
         ]
       };
     };

   /** CollectionPage + ItemList of advisors + BreadcrumbList for /about/advisors. */
   static advisorListSchema = (pageUrl: string, name: string, description: string, people: { name: string; url: string; jobTitle: string; image?: string }[]) =>
     {
       return {
         '@context': 'https://schema.org',
         '@graph': [
           {
             '@type'     : 'CollectionPage',
             '@id'       : pageUrl,
             url         : pageUrl,
             name        : name,
             description : description,
             inLanguage  : 'en-US',
             isPartOf    : { '@id': WEBSITE_ID },
             about       : { '@id': ORG_ID },
             breadcrumb  : { '@id': pageUrl + '#breadcrumb' },
             mainEntity  : { '@id': pageUrl + '#advisors' }
           },
           {
             '@type': 'ItemList',
             '@id'  : pageUrl + '#advisors',
             name   : name,
             numberOfItems: people.length,
             itemListElement: people.map((p, i) => ({
               '@type'  : 'ListItem',
               position : i + 1,
               item     : {
                 '@type'  : 'Person',
                 '@id'    : p.url + '#person',
                 name     : p.name,
                 url      : p.url,
                 jobTitle : p.jobTitle,
                 ...(p.image ? { image: p.image } : {}),
                 worksFor : { '@id': ORG_ID }
               }
             }))
           },
           {
             '@type': 'BreadcrumbList',
             '@id'  : pageUrl + '#breadcrumb',
             itemListElement: [
               { '@type': 'ListItem', position: 1, name: 'Home',      item: SITE_URL + '/' },
               { '@type': 'ListItem', position: 2, name: 'About IFA', item: SITE_URL + '/about' },
               { '@type': 'ListItem', position: 3, name: name }
             ]
           }
         ]
       };
     };

     static videoSchema = (name : string, url :string, id : string, publishDate: string, modifiedDate: string, author: string, keywords: string, description: string, text: string, imageUrl: string, phoneLocal: string, faxNumber: string, mailingAddress: string, views:number, video:string) => {
      return {
        '@context'  :
          'https://schema.org',
        '@type'     :
          'VideoObject',
        '@id'       : url + '#video',
        mainEntityOfPage: { '@type': 'WebPage', '@id': url },
        url         : url,
        name        : name, 
        datePublished: publishDate, 
        dateModified: modifiedDate,
        uploadDate: publishDate,       
        author      : authorsFrom(author),       
        description : description,
        keywords    : keywords,
        embedUrl : video,
        thumbnailUrl: imageUrl,
        thumbnail   : imageUrl,
        inLanguage  : 'en-US',
        isAccessibleForFree: true,
        'publisher' : { ...PUBLISHER, telephone: phoneLocal, faxNumber: faxNumber },
        'interactionStatistic':[{
            '@type':    'InteractionCounter',
            'interactionType': 'https://schema.org/WatchAction',
            'userInteractionCount': views
        }]
      };
    };

   
   
   getOrganizationGraph(): Record<string, any> {
     return JsonldserviceService.organizationGraph();
   }
   getAdvisorSchema(input: AdvisorSchemaInput): Record<string, any> {
     return JsonldserviceService.advisorSchema(input);
   }
   getAdvisorListSchema(pageUrl: string, name: string, description: string, people: { name: string; url: string; jobTitle: string; image?: string }[]): Record<string, any> {
     return JsonldserviceService.advisorListSchema(pageUrl, name, description, people);
   }
   getArticleSchema(name : string, url :string, id : string, publishDate: string, modifiedDate: string, author: string, keywords: string, description: string, text: string, imageUrl: string, phoneLocal: string, faxNumber: string, mailingAddress: string, views:number): Record<string, any> {
     return JsonldserviceService.articleSchema(name , url, id , publishDate, modifiedDate, author, keywords, description, text, imageUrl, phoneLocal, faxNumber, mailingAddress, views);
   }   
   getPageSchema(name : string, url :string, description: string): Record<string, any> {
    return JsonldserviceService.pageSchema(name, url, description);
  }
   getVideoSchema(name : string, url :string, id : string, publishDate: string, modifiedDate: string, author: string, keywords: string, description: string, text: string, imageUrl: string, phoneLocal: string, faxNumber: string, mailingAddress: string, views:number, video:string): Record<string, any> {
    return JsonldserviceService.videoSchema(name , url, id , publishDate, modifiedDate, author, keywords, description, text, imageUrl, phoneLocal, faxNumber, mailingAddress, views, video);
  }

   /** Removes page-level schemas only; the site-wide org graph (ORG_CLASS) persists across navigation. */
   removeStructuredData(): void {
       const els = Array.from(this._document.head.getElementsByClassName(JsonldserviceService.PAGE_CLASS));
       els.forEach(el => this._document.head.removeChild(el));
	 }

   insertOrganizationSchema(): void {
     this.insertSchema(JsonldserviceService.organizationGraph(), JsonldserviceService.ORG_CLASS);
   }

   /** One <script> per className; the payload is replaced (never appended) so the tag always holds valid JSON. */
   insertSchema(schema: Record<string, any>, className = JsonldserviceService.PAGE_CLASS): void {
     const head = this._document.head;
     let script = head.querySelector<HTMLScriptElement>(`script.${className}`);
     if (!script) {
       script = this._document.createElement('script');
       script.setAttribute('class', className);
       script.setAttribute('type', JsonldserviceService.scriptType);
       head.appendChild(script);
     }
     script.textContent = JSON.stringify(schema);
   }

  // getPageSchema(a:any, b:any, c:any){}
  // removeStructuredData(){}
  // insertSchema(s:any){}

}
